import { useSnackbar } from 'notistack';
import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useAccount } from 'queries/accounts/useAccount';
import { useTicketOffersByCode } from 'queries/tickets/useTicketOffersByCode';
import { useCartStore, useCartStoreActions } from 'services/stores/cart';

export const useTicketOffers = () => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const { account, isSignedIn } = useAccount();
  const { setTicketOffer } = useCartStoreActions();
  const ticketOffer = useCartStore((state) => state.ticketOffer);

  const shouldFetchOffers = useMemo(() => {
    //If user is signed in and the ticketOffer has a customerLoyaltyId, only enable if the loyaltyIds match
    if (isSignedIn && ticketOffer?.customerLoyaltyId) {
      const match =
        account?.ticketingLoyaltyId === ticketOffer.customerLoyaltyId;

      return {
        enabled: match,
        loyaltyMismatch: !match,
      };
    }

    //fallback to only checking if there's a code
    return {
      enabled: !!ticketOffer?.code,
      loyaltyMismatch: false,
    };
  }, [
    isSignedIn,
    account?.ticketingLoyaltyId,
    ticketOffer?.code,
    ticketOffer?.customerLoyaltyId,
  ]);

  useEffect(() => {
    if (shouldFetchOffers.loyaltyMismatch) {
      enqueueSnackbar(
        intl.formatMessage({ id: 'ticket_offer.account.no_match' }),
        { variant: 'error' },
      );

      setTicketOffer(undefined); // cleanup
    }

    // Only want to trigger this when shouldFetchOffers and ticketOffer
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetchOffers, ticketOffer?.code]);

  const { ticketOffers, isLoading } = useTicketOffersByCode(
    {
      code: ticketOffer?.code,
    },
    {
      enabled: shouldFetchOffers.enabled,
    },
  );

  return {
    ticketOffers,
    isLoading,
    isEnabled: !!ticketOffer?.code,
  };
};
